/* A self-contained, client-side evaluator for the BeX expression language —
 * enough to let the guide's playgrounds compute results live. It mirrors the
 * spec (EXPRESSION-LANGUAGE.md): the closed 24-function set, the operator set +
 * precedence, Python-style truthiness, and the ABSENT sentinel. Deterministic;
 * NOT a security boundary (the real `hash()` is pepper'd server-side — here it's
 * a stable demo digest). Hand-written tokenizer so it compiles to ES5. */

export interface EvalResult { ok: boolean; value?: unknown; error?: string; }

export const ABSENT = Symbol("ABSENT");

type Tok = { k: string; v: unknown };

const TWO = ["==", "!=", ">=", "<=", "&&", "||"];
const ONE = "+-*/<>!(),";

const WORDS: Record<string, Tok> = {
  true: { k: "lit", v: true }, false: { k: "lit", v: false }, null: { k: "lit", v: null },
  and: { k: "op", v: "&&" }, or: { k: "op", v: "||" }, not: { k: "op", v: "!" },
};

const PREC: Record<string, number> = {
  "||": 1, "&&": 2, "==": 3, "!=": 3, "<": 3, ">": 3, "<=": 3, ">=": 3, "+": 4, "-": 4, "*": 5, "/": 5,
};

function isIdentStart(c: string) { return /[A-Za-z_]/.test(c); }
function isIdent(c: string) { return /[A-Za-z0-9_]/.test(c); }
function isDigit(c: string) { return c >= "0" && c <= "9"; }

function lex(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i++; continue; }
    // number
    if (isDigit(c)) {
      let j = i + 1;
      while (j < src.length && (isDigit(src[j]!) || src[j] === ".")) j++;
      out.push({ k: "lit", v: parseFloat(src.slice(i, j)) }); i = j; continue;
    }
    // string
    if (c === "'" || c === '"') {
      let j = i + 1; let s = "";
      while (j < src.length && src[j] !== c) { s += src[j]; j++; }
      if (j >= src.length) throw new Error("unterminated string");
      out.push({ k: "lit", v: s }); i = j + 1; continue;
    }
    // path: $ . ident ( . ident | [n] )*
    if (c === "$") {
      let j = i + 1; let p = "$";
      while (j < src.length) {
        if (src[j] === ".") { p += "."; j++; let s = ""; while (j < src.length && isIdent(src[j]!)) { s += src[j]; j++; } p += s; }
        else if (src[j] === "[") { let s = "["; j++; while (j < src.length && src[j] !== "]") { s += src[j]; j++; } s += "]"; j++; p += s; }
        else break;
      }
      out.push({ k: "path", v: p }); i = j; continue;
    }
    // two-char operators
    const two = src.slice(i, i + 2);
    if (TWO.indexOf(two) >= 0) { out.push({ k: "op", v: two }); i += 2; continue; }
    // one-char operators / punctuation
    if (ONE.indexOf(c) >= 0) { out.push({ k: "op", v: c }); i += 1; continue; }
    // identifier (function name or word)
    if (isIdentStart(c)) {
      let j = i + 1; while (j < src.length && isIdent(src[j]!)) j++;
      const w = src.slice(i, j);
      out.push(WORDS[w.toLowerCase()] ?? { k: "ident", v: w });
      i = j; continue;
    }
    throw new Error("unexpected character: " + c);
  }
  out.push({ k: "end", v: null });
  return out;
}

function truthy(v: unknown): boolean {
  if (v === ABSENT || v == null || v === false || v === 0 || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return Boolean(v);
}

function walk(ctx: unknown, path: string): unknown {
  // path looks like $.a.b[0].c
  const body = path.slice(1); // drop $
  let cur: unknown = ctx;
  const re = /\.([A-Za-z_][A-Za-z0-9_]*)|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (cur == null) return null;
    if (m[2] !== undefined) cur = Array.isArray(cur) ? (cur as unknown[])[parseInt(m[2], 10)] : null;
    else if (Array.isArray(cur)) cur = (cur as unknown[]).map((x) => (x && typeof x === "object") ? ((x as Record<string, unknown>)[m![1]!] ?? null) : null); // implicit map: list.field -> [field…]
    else cur = typeof cur === "object" ? (cur as Record<string, unknown>)[m[1]!] : null;
    if (cur === undefined) cur = null;
  }
  return cur;
}

const num = (x: unknown): number => { const n = Number(x); if (Number.isNaN(n)) throw new Error("not a number: " + String(x)); return n; };
const str = (x: unknown): string => x == null ? "" : String(x);
const arr = (x: unknown): unknown[] => Array.isArray(x) ? x : [];

function pad(n: number) { return n < 10 ? "0" + n : "" + n; }
function fmtDate(iso: unknown): string {
  const d = new Date(str(iso));
  if (isNaN(d.getTime())) return str(iso);
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

const FUNCS: Record<string, (a: unknown[]) => unknown> = {
  concat: (a) => a.map(str).join(""),
  sum: (a) => arr(a[0]).reduce((s: number, x) => s + num(x), 0),
  len: (a) => Array.isArray(a[0]) ? (a[0] as unknown[]).length : str(a[0]).length,
  count: (a) => arr(a[0]).length,
  upper: (a) => str(a[0]).toUpperCase(),
  lower: (a) => str(a[0]).toLowerCase(),
  trim: (a) => str(a[0]).trim(),
  add_days: (a) => { const d = new Date(str(a[0])); d.setDate(d.getDate() + num(a[1])); return d.toISOString().slice(0, 10); },
  if: (a) => truthy(a[0]) ? a[1] : a[2],
  contains: (a) => Array.isArray(a[0]) ? (a[0] as unknown[]).indexOf(a[1]) >= 0 : str(a[0]).indexOf(str(a[1])) >= 0,
  get: (a) => { const o = a[0]; const k = str(a[1]); return o && typeof o === "object" ? (o as Record<string, unknown>)[k] ?? null : null; },
  round: (a) => { const f = a[1] == null ? 0 : num(a[1]); const p = Math.pow(10, f); return Math.round(num(a[0]) * p) / p; },
  default: (a) => (a[0] == null || a[0] === ABSENT) ? a[1] : a[0],
  absent: () => ABSENT,
  when: (a) => truthy(a[0]) ? a[1] : ABSENT,
  push: (a) => [...arr(a[0]), a[1]],
  merge_lists: (a) => [...arr(a[0]), ...arr(a[1])],
  split: (a) => str(a[0]).split(str(a[1])),
  join: (a) => arr(a[0]).map(str).join(str(a[1])),
  to_number: (a) => num(a[0]),
  to_text: (a) => str(a[0]),
  escape_regex: (a) => str(a[0]).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  format_date: (a) => fmtDate(a[0]),
  format_number: (a) => { const n = num(a[0]); const f = a[1] == null ? undefined : num(a[1]); return f == null ? n.toLocaleString() : n.toLocaleString(undefined, { minimumFractionDigits: f, maximumFractionDigits: f }); },
  hash: (a) => { let h = 5381; const s = str(a[0]); for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; return "demo$" + h.toString(16); },
};

class Parser {
  pos = 0;
  constructor(private t: Tok[], private ctx: unknown) {}
  private peek() { return this.t[this.pos]!; }
  private next() { return this.t[this.pos++]!; }
  parse(): unknown { const v = this.expr(1); if (this.peek().k !== "end") throw new Error("unexpected token"); return v; }
  private expr(min: number): unknown {
    let left = this.atom();
    for (;;) {
      const t = this.peek();
      if (t.k !== "op" || PREC[t.v as string] === undefined || PREC[t.v as string]! < min) return left;
      this.next();
      const right = this.expr(PREC[t.v as string]! + 1);
      left = this.apply(t.v as string, left, right);
    }
  }
  private atom(): unknown {
    const t = this.next();
    if (t.k === "lit") return t.v;
    if (t.k === "path") return walk(this.ctx, t.v as string);
    if (t.k === "op" && t.v === "!") return !truthy(this.atom());
    if (t.k === "op" && t.v === "-") return -num(this.atom());
    if (t.k === "op" && t.v === "(") { const e = this.expr(1); this.expect(")"); return e; }
    if (t.k === "ident") {
      const name = t.v as string;
      const fn = FUNCS[name];
      if (!fn) throw new Error("unknown function: " + name);
      this.expect("(");
      const args: unknown[] = [];
      if (!(this.peek().k === "op" && this.peek().v === ")")) {
        args.push(this.expr(1));
        while (this.peek().k === "op" && this.peek().v === ",") { this.next(); args.push(this.expr(1)); }
      }
      this.expect(")");
      return fn(args);
    }
    throw new Error("unexpected token");
  }
  private expect(op: string) { const t = this.next(); if (!(t.k === "op" && t.v === op)) throw new Error("expected " + op); }
  private apply(op: string, a: unknown, b: unknown): unknown {
    switch (op) {
      case "||": return truthy(a) || truthy(b);
      case "&&": return truthy(a) && truthy(b);
      case "==": return a === b;
      case "!=": return a !== b;
      case ">": return num(a) > num(b);
      case "<": return num(a) < num(b);
      case ">=": return num(a) >= num(b);
      case "<=": return num(a) <= num(b);
      case "+": return num(a) + num(b);
      case "-": return num(a) - num(b);
      case "*": return num(a) * num(b);
      case "/": { const d = num(b); if (d === 0) throw new Error("division by zero"); return num(a) / d; }
    }
    throw new Error("bad operator " + op);
  }
}

export function evaluate(src: string, ctx: Record<string, unknown>): EvalResult {
  try {
    if (!src.trim()) return { ok: false, error: "empty expression" };
    const v = new Parser(lex(src), ctx).parse();
    return { ok: true, value: v };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Render a value for display in the playground (ABSENT shown explicitly). */
export function showValue(v: unknown): string {
  if (v === ABSENT) return "ABSENT (key/element dropped)";
  if (v === null || v === undefined) return "null";
  if (typeof v === "string") return JSON.stringify(v);
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}
